/**
 * CommunityComposer — bottom-of-page input row for the community wall.
 *
 * Ports Flutter `bottomInputActions` / `inputWidget`
 * (`community_screen.dart:1240-1379`): a text field with a dynamic hint plus a
 * send button. The task prompt (line 4) explicitly asks for a custom composer
 * analogous to `ChatComposer` that reuses the shared `InputText` primitive — so
 * this component is modeled on `ChatComposer`
 * (`@/presentation/feature/chat/components/ChatComposer`) but reuses the common
 * `MessageInput` (which wraps `InputText`) directly: community has no
 * add-media / gift / blocked-composer branches to fold into a chat-local input.
 *
 * The add-media button (`community_screen.dart:1331-1344`) is now wired
 * (`handleAddMedia` below): it snapshots the draft + reply target + tap
 * timestamp, clears the reply, and opens the shared `AddContentSheet`
 * community media flow, adapting its send callback onto `onSendImage`
 * (`useCommunity.sendImage`) or `onSendVideo` (`useCommunity.sendVideo`) by the
 * collected `mediaType` — Flutter's `_addMediaMessage` is ONE media path for
 * both (`community_screen.dart:398-461`).
 *
 * The in-line go-live button (`GoLiveButtonWidget`,
 * `community_screen.dart:1351-1368`) is wired via `onGoLive`: it renders inside
 * `MessageInput`'s action row (between add-media and send) but ONLY when the
 * current user owns the wall (`isCurrentUser` — parity with Flutter's
 * `widget.user.firebaseId == currentUser.uid` gate). The composer stays dumb:
 * the confirm-sheet + navigation orchestration lives on the page
 * (`CommunityPage.handleGoLive` → `launchBroadcasterGoLive`), mirroring the
 * `SelectContentTypeSheetContent` go-live button.
 *
 * The dynamic hint mirrors `community_screen.dart:1276-1288`:
 *  - owner of an empty wall (`isCurrentUser && !hasMore && messages.isEmpty`) →
 *    `"Welcome your community..."`,
 *  - no reply target → `"Send a message..."`,
 *  - reply target set → `"Reply to <userName>"`.
 *
 * The whole composer is hidden during first load
 * (`community_screen.dart:1241` — `Visibility(visible: !_isFirstLoad, ...)`).
 * The page already gates the surface behind its loading indicator, so this
 * branch simply renders `null`.
 *
 * The draft clears synchronously after a successful dispatch via the imperative
 * `MessageInputHandle` (Flutter `_textEditingController.clear()`,
 * `community_screen.dart:538`), matching `ChatComposer.handleSend`.
 *
 * Deviation from Flutter: the reply context surfaces as the dedicated
 * `CommunityReplyBar` above the input (with an explicit dismiss control) in
 * addition to the dynamic hint. Flutter shows the reply only via the hint and
 * clears it by tapping the dimming overlay (`community_screen.dart:1402-1406`);
 * a desktop viewport has no tap-overlay affordance, so the bar is the web
 * equivalent. Business semantics are unchanged (owned by `setReplyTarget`).
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import {
  MessageInput,
  type MessageInputHandle,
} from '@/presentation/components/common/MessageInput';
import { AddContentSheet } from '@/presentation/feature/publish/components/sheets/addContent/AddContentSheet';
import { canOpenAddContent } from '@/presentation/feature/publish/utils/canOpenAddContent';
import { MediaType } from '@/domain/enums/mediaType';
import { CommunityReplyBar } from './CommunityReplyBar';
import type { SendCommunityImageArgs, SendCommunityVideoArgs } from '../hooks/useCommunity';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';

export interface CommunityComposerProps {
  /**
   * Hook's `isFirstLoad` (Flutter `_isFirstLoad`). The composer is hidden during
   * first load (`community_screen.dart:1241`); render `null` while true.
   */
  isFirstLoad: boolean;
  /** True iff the current user is the wall owner (`community_screen.dart:1277-1279`). */
  isCurrentUser: boolean;
  /** True iff a further page exists (`_hasMore`, `community_screen.dart:1280`). */
  hasMore: boolean;
  /** True iff the wall has no messages — drives the owner welcome hint (`_messages.isEmpty`, `community_screen.dart:1281`). */
  hasNoMessage: boolean;
  /** The message currently being replied to, or `null` (`_messageToReplyNotifier`). */
  messageToReply: CommunityChatMessageEntity | null;
  /** Send plaintext. Wired by host to `useCommunity.sendText`; the draft clears synchronously after dispatch. */
  onSendText: (text: string) => void;
  /** Clears the reply target. Wired by host to `useCommunity.setReplyTarget(null)`. */
  onClearReply: () => void;
  /**
   * Screen key for `AddContentSheet.show` association (add-media flow). Threaded
   * from the page's `screenKey` mint.
   */
  screenKey: string;
  /**
   * Send an image message. Wired by host to `useCommunity.sendImage`. The
   * add-media adapter closes over the tap-time snapshot (draft text, timestamp,
   * reply target) and forwards it here after the add-content sheet confirms when
   * the picked media is an IMAGE. Resolves the upload-success boolean so the sheet
   * can toast on failure.
   */
  onSendImage: (args: SendCommunityImageArgs) => Promise<boolean>;
  /**
   * Send a video message. Wired by host to `useCommunity.sendVideo`. Same
   * add-media adapter + tap-time snapshot as `onSendImage`; the adapter routes to
   * this callback (adding the collected `durationSeconds`) when the picked media
   * is a VIDEO (`MediaType.VIDEO`) — Flutter's `_addMediaMessage` is ONE media
   * path for both (`community_screen.dart:398-461`). Resolves the upload-success
   * boolean so the sheet can toast on failure.
   */
  onSendVideo: (args: SendCommunityVideoArgs) => Promise<boolean>;
  /**
   * Broadcaster go-live entry (Flutter `_goLive`, `community_screen.dart:815-832`).
   * Wired by the host to `CommunityPage.handleGoLive` (confirm → mint → launch as
   * broadcaster). Surfaced in the composer only when `isCurrentUser` — parity with
   * Flutter's `widget.user.firebaseId == currentUser.uid` gate
   * (`community_screen.dart:1352-1356`).
   */
  onGoLive: () => void;
  /**
   * When true, the go-live button is rendered DISABLED (visible, non-interactive)
   * — the note-2 guard: the wall owner is already broadcasting a LIVE session (in
   * this tab or another same-origin tab), so a second go-live must be blocked.
   * Layers on top of the `isCurrentUser` gate (only the owner sees go-live at
   * all); a non-owner never sees the affordance, disabled or otherwise. Defaults
   * to `false` (not broadcasting) at the call site. Web-only — one live screen at
   * a time on mobile means this case never arises in Flutter.
   */
  goLiveDisabled?: boolean;
}

export const CommunityComposer: React.FC<CommunityComposerProps> = ({
  isFirstLoad,
  isCurrentUser,
  hasMore,
  hasNoMessage,
  messageToReply,
  onSendText,
  onClearReply,
  screenKey,
  onSendImage,
  onSendVideo,
  onGoLive,
  goLiveDisabled = false,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Imperative handle to clear the input synchronously after dispatch, matching
  // Flutter `_textEditingController.clear()` (`community_screen.dart:538`) and
  // `ChatComposer.handleSend` (`ChatComposer.tsx:117-125`).
  const messageInputRef = useRef<MessageInputHandle>(null);

  // Focus the composer input when a reply target is set, mirroring Flutter
  // `_onReplyTap` → `_focusNode.requestFocus()` (`community_screen.dart:743-744`).
  // Only on the `null → set` transition (not on every reply-target change) so
  // re-targeting while the field is already focused does not fight the cursor.
  const prevReplyRef = useRef<CommunityChatMessageEntity | null>(null);
  useEffect(() => {
    if (messageToReply !== null && prevReplyRef.current === null) {
      messageInputRef.current?.focus();
    }
    prevReplyRef.current = messageToReply;
  }, [messageToReply]);

  const handleSend = useCallback(
    (text: string): void => {
      // Trim is the empty-guard ONLY (`community_screen.dart:472-474`); the raw,
      // untrimmed controller text is persisted (`community_screen.dart:523`), so
      // forward the original `text` (leading/trailing whitespace preserved).
      if (text.trim() === '') return;
      onSendText(text);
      messageInputRef.current?.clear();
    },
    [onSendText],
  );

  // The dimissed reply target clears via the reply bar's dismiss control —
  // Flutter clears it by tapping the dimming overlay
  // (`community_screen.dart:1402-1406`).
  const handleDismissReply = useCallback((): void => {
    onClearReply();
  }, [onClearReply]);

  // Add-media tap (Flutter `_addMediaMessage`, `community_screen.dart:398-446`):
  // snapshot THREE values at tap time, BEFORE opening the sheet — the timestamp
  // (`:399`, persisted as `createdTimestamp` at `:446`, so the wall sort position
  // is the tap time regardless of picker dwell), the composer text (`:443`), and
  // the reply target (`:400`). Then clear the reply target (Flutter `didPushNext`
  // parity, `:176-183` — the sheet is not a route, so `onPageHidden` never fires;
  // a canceled sheet leaves the reply cleared in BOTH clients). The draft is
  // deliberately NOT cleared: Flutter never clears `_textEditingController` on the
  // media path (contrast the text send's `.clear()` at `:536`), so the captured
  // draft rides along as the image message's `text` and the composer keeps it.
  const handleAddMedia = useCallback((): void => {
    // Upload-in-progress guard FIRST, ahead of the snapshot + reply clear: Flutter
    // runs it inside `navigateToAddContentScreen`
    // (`navigation_service.dart:420-435`), which short-circuits before any route is
    // pushed — so `didPushNext` never fires and the reply target survives a blocked
    // tap (`community_screen.dart:459` → `:176-183`). Clearing before the guard
    // would drop the reply on web only.
    if (!canOpenAddContent({ l10n, screenKey })) return;
    const capturedAtMillis = Date.now();
    const capturedText = messageInputRef.current?.getValue() ?? '';
    const capturedReply = messageToReply ?? undefined;
    onClearReply();
    AddContentSheet.show({
      screenKey,
      width: responsive.addContentSheet.width,
      fixedHeight: responsive.addContentSheet.fixedHeight,
      community: {
        // Adapter closing over the tap-time snapshot: the sheet resolves the
        // picked media, this bridges it onto `useCommunity.sendImage` /
        // `.sendVideo` by the collected `mediaType`. The tap-time snapshot
        // (`capturedText` / `capturedAtMillis` / `capturedReply`) is shared by
        // both — Flutter's `_addMediaMessage` is ONE media path for both
        // (`community_screen.dart:398-461`).
        onSendMedia: (args) =>
          args.mediaType === MediaType.VIDEO
            ? onSendVideo({
                file: args.file,
                aspectRatio: args.aspectRatio,
                durationSeconds: args.durationSeconds,
                text: capturedText,
                capturedAtMillis,
                replyToMessage: capturedReply,
              })
            : onSendImage({
                file: args.file,
                aspectRatio: args.aspectRatio,
                text: capturedText,
                capturedAtMillis,
                replyToMessage: capturedReply,
              }),
      },
    });
  }, [
    l10n,
    messageToReply,
    onClearReply,
    screenKey,
    responsive.addContentSheet.width,
    responsive.addContentSheet.fixedHeight,
    onSendImage,
    onSendVideo,
  ]);

  // Composer hidden during first load (`community_screen.dart:1241`). The page
  // gates the surface behind its loading indicator, so render nothing here.
  if (isFirstLoad) {
    return null;
  }

  // Dynamic hint (`community_screen.dart:1276-1288`):
  //  1. owner of an empty wall → welcome hint,
  //  2. no reply target → default hint,
  //  3. reply target set → "Reply to <userName>".
  let placeholder: string;
  if (isCurrentUser && !hasMore && hasNoMessage) {
    placeholder = l10n.community.composerHintWelcome;
  } else if (messageToReply === null) {
    placeholder = l10n.community.composerHintDefault;
  } else {
    placeholder = l10n.community.composerHintReply.replace(
      '{name}',
      messageToReply.messageUsersInfo.userName,
    );
  }

  return (
    <div
      style={{
        width: '100%',
        backgroundColor: theme.background,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {/* Reply context (web addition — see file header). Rendered only when a
          target is set (`community_screen.dart:1273-1288`). */}
      {messageToReply !== null && (
        <CommunityReplyBar
          messageToReply={messageToReply}
          onDismiss={handleDismissReply}
        />
      )}

      {/* The add-media and go-live buttons render inside `MessageInput` (via
          `onMediaTap` / `onGoLive`), between the input and the send button — the
          slot where Flutter places them (`community_screen.dart:1331-1368`). The
          go-live affordance is surfaced only for the wall owner (`isCurrentUser`),
          parity with Flutter's `widget.user.firebaseId == currentUser.uid` gate
          (`community_screen.dart:1352-1356`). */}

      <MessageInput
        ref={messageInputRef}
        onSend={handleSend}
        disabled={false}
        loading={false}
        placeholder={placeholder}
        backgroundColor={theme.background}
        qa={{ id: 'community-message-input' }}
        sendQa={{ id: 'community-message-send' }}
        onMediaTap={handleAddMedia}
        mediaQa={{ id: 'community-composer-attach' }}
        onGoLive={isCurrentUser ? onGoLive : undefined}
        goLiveDisabled={goLiveDisabled}
        goLiveQa={{ id: 'community-composer-golive' }}
      />
    </div>
  );
};
